import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/** The billing screens' shared pieces: the page frame, card and section
 *  headings, and the button shapes. */

/**
 * Plans & billing and Credits Usage share one frame: white, a back link
 * (home's "All Rooms" by default), and nothing else in the bar — the balance and the account live on
 * home's own top bar.
 */
export function BillingFrame({
  back = { label: 'All Rooms', to: '/' },
  children,
}: {
  /** Where the back link goes; home unless somewhere sent the user here. */
  back?: { label: string; state?: unknown; to: string };
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <main className="min-h-svh bg-surface">
      <div className="mx-auto w-full max-w-[1056px] px-6 pt-8 pb-16">
        <button
          className="-ml-1 flex cursor-pointer items-center gap-1 text-ui font-semibold text-text-primary transition-colors hover:text-text-secondary"
          onClick={() => {
            void navigate(back.to, { state: back.state });
          }}
          type="button"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {back.label}
        </button>
        {children}
      </div>
    </main>
  );
}

/**
 * `!`-prefixed colour and size: `index.css` styles bare `h1`/`h2` unlayered,
 * which beats layered Tailwind utilities — without the bang these headings
 * inherit `--text-h`, which flips to near-white under a dark OS theme while
 * the page itself stays light (CLAUDE.md > Styling).
 */
export function PageTitle({ children }: { children: ReactNode }) {
  return (
    <h1 className="!m-0 !text-home-title !font-bold !text-text-primary">
      {children}
    </h1>
  );
}

export function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h2 className="!m-0 font-heading !text-heading-sm !font-bold !tracking-tight !text-text-primary">
      {children}
    </h2>
  );
}

/** "CURRENT PLAN", "CREDITS" — a card's small caps label. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-small-label text-text-secondary uppercase">{children}</p>
  );
}

/** A white card on a hairline, the page's one container shape. */
export const CARD = 'rounded-tile border border-hairline bg-surface';

/** The violet chip: "Current", "Save 14%". */
export const VIOLET_CHIP =
  'rounded-pill bg-violet-soft px-2 py-0.5 text-tag font-semibold text-room-violet';

/**
 * The app's button shapes, as class strings rather than a component. `bg-ink`
 * is this product's primary fill — `brand` is an accent for emphasis and
 * alarm, never the main action.
 */
export const PRIMARY_BUTTON =
  'flex h-11 shrink-0 items-center justify-center gap-2 rounded-button bg-ink px-5 text-ui-strong font-bold text-white transition duration-200 not-disabled:cursor-pointer hover:not-disabled:bg-ink/90 active:not-disabled:scale-[0.98] disabled:opacity-35';

export const SECONDARY_BUTTON =
  'flex h-10 shrink-0 items-center justify-center gap-2 rounded-button border border-hairline-alt bg-surface px-4 text-ui font-bold text-text-primary transition duration-200 not-disabled:cursor-pointer hover:not-disabled:bg-surface-hover active:not-disabled:scale-[0.98] disabled:opacity-35';

/** A pack's "Buy": the secondary shape, turning Floto red on hover. */
export const BUY_BUTTON =
  'flex h-10 shrink-0 items-center justify-center gap-2 rounded-button border border-hairline-alt bg-surface px-4 text-ui font-bold text-text-primary transition duration-200 not-disabled:cursor-pointer hover:not-disabled:border-brand hover:not-disabled:bg-brand hover:not-disabled:text-white active:not-disabled:scale-[0.98] disabled:opacity-35';

/** Borderless, beside a secondary button: "Billing details". */
export const TEXT_BUTTON =
  'flex h-10 shrink-0 items-center gap-1.5 px-2 text-ui font-semibold text-text-secondary transition-colors not-disabled:cursor-pointer hover:not-disabled:text-text-primary disabled:opacity-35';

/** The round outline pill in the page's top-right: "Usage". */
export const PILL_BUTTON =
  'flex h-10 shrink-0 cursor-pointer items-center rounded-pill border border-hairline-alt bg-surface px-4 text-ui font-semibold text-text-secondary transition hover:bg-surface-hover hover:text-text-primary';
